/**
 * R-130-9 — billing aggregate storage layout.
 *
 * Canonical path:
 *   - billingDailyUsers/{date}__{uid} where date is also stored as a field and queried via where('date', '==', YYYY-MM-DD)
 *
 * Both writer and reader read THIS comment. If you change the layout, update both AND the firestore.rules
 * for the corresponding path AND the BillingDashboard component AND the secrets registry if any new env var is involved.
 */
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

export interface DailyUserCostAggregate {
  uid: string;
  date: string;
  companyId?: string | null;
  totalCostUsd: number;
  callCount: number;
  promptTokens: number;
  /** Visible response tokens only. */
  responseTokens: number;
  /**
   * Thinking tokens (billed at the output rate; already included in
   * totalCostUsd). 0 for rows written before 2026-09-30, which neither
   * counted nor priced thinking.
   */
  thoughtsTokens?: number;
  totalTokens: number;
  byModel?: Record<string, {
    costUsd?: number;
    callCount?: number;
    promptTokens?: number;
    responseTokens?: number;
    thoughtsTokens?: number;
  }>;
  updatedAt?: string;
}

export interface TopSpenderRow {
  uid: string;
  companyId?: string | null;
  totalCostUsd: number;
  callCount: number;
  totalTokens: number;
}

// R-130-9: canonical aggregate collection path.
const BILLING_DAILY_USERS_COLLECTION = 'billingDailyUsers';

function dailyAggregateDocId(date: string, uid: string): string {
  // R-130-9: stable doc id for idempotent per-user-per-day increments.
  return `${date}__${uid}`;
}

function dailyUsersCollection() {
  return collection(db, BILLING_DAILY_USERS_COLLECTION);
}

function dailyUsersByDateQuery(date: string) {
  return query(dailyUsersCollection(), where('date', '==', date));
}

function normalizeAggregate(uid: string, date: string, data: Record<string, unknown>): DailyUserCostAggregate {
  const resolvedUid = typeof data.uid === 'string' ? data.uid : uid;
  const resolvedDate = typeof data.date === 'string' ? data.date : date;
  return {
    uid: resolvedUid,
    date: resolvedDate,
    companyId: typeof data.companyId === 'string' ? data.companyId : null,
    totalCostUsd: Number(data.totalCostUsd ?? 0),
    callCount: Number(data.callCount ?? 0),
    promptTokens: Number(data.promptTokens ?? 0),
    responseTokens: Number(data.responseTokens ?? 0),
    thoughtsTokens: Number(data.thoughtsTokens ?? 0),
    totalTokens: Number(data.totalTokens ?? 0),
    byModel: (data.byModel as DailyUserCostAggregate['byModel']) ?? {},
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : undefined,
  };
}

export async function getDailyCostForUser(
  uid: string,
  date: string,
): Promise<DailyUserCostAggregate | null> {
  // R-130-9: read from flat canonical doc id.
  const snap = await getDoc(doc(db, BILLING_DAILY_USERS_COLLECTION, dailyAggregateDocId(date, uid)));
  if (!snap.exists()) return null;
  return normalizeAggregate(uid, date, snap.data() as Record<string, unknown>);
}

/** Sum daily spend for all users tagged with the given companyId. */
export async function getDailyCostForCustomer(
  companyId: string,
  date: string,
): Promise<number> {
  const snap = await getDocs(dailyUsersByDateQuery(date));
  let total = 0;
  for (const row of snap.docs) {
    const data = row.data();
    if (data.companyId === companyId) {
      total += Number(data.totalCostUsd ?? 0);
    }
  }
  return total;
}

/** Top spenders for a calendar day (UTC date string YYYY-MM-DD). */
export async function getTopSpenders(
  date: string,
  maxRows = 25,
): Promise<TopSpenderRow[]> {
  // R-130-9: sort in-memory to avoid relying on composite indexes.
  const snap = await getDocs(dailyUsersByDateQuery(date));
  return snap.docs.map(d => {
    const data = d.data();
    const uid = typeof data.uid === 'string' ? data.uid : d.id;
    return {
      uid,
      companyId: typeof data.companyId === 'string' ? data.companyId : null,
      totalCostUsd: Number(data.totalCostUsd ?? 0),
      callCount: Number(data.callCount ?? 0),
      totalTokens: Number(data.totalTokens ?? 0),
    };
  }).sort((a, b) => b.totalCostUsd - a.totalCostUsd).slice(0, maxRows);
}

/** List available aggregate dates (parent doc ids) — best-effort via collection group is not used; caller passes dates. */
export async function listDailyUserAggregates(date: string): Promise<DailyUserCostAggregate[]> {
  const snap = await getDocs(dailyUsersByDateQuery(date));
  return snap.docs.map(d =>
    normalizeAggregate(d.id, date, d.data() as Record<string, unknown>),
  );
}
